import { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { enrollWithPayHero, lmsGetEnrollmentStatus } from '@/lib/api';
import { useCurrency } from '@/contexts/CurrencyContext';

const POLL_INTERVAL_MS = 2500;

interface EnrollModalProps {
  open: boolean;
  onClose: () => void;
  courseTitle: string;
  courseId: string;
  priceKes: number;
  isAuthenticated: boolean;
  onSignIn: () => void;
  userEmail?: string;
  userName?: string;
  /** Called when user clicks "Start learning" after successful enrollment. Parent should refetch enrollment state and optionally navigate to first section. */
  onStartLearning?: () => void;
  /** When provided with onEnrollmentConfirmed, we poll for enrollment after STK and auto-switch to success when callback is processed. Admin has no learner; omit or return null to skip polling. */
  getLearnerId?: () => Promise<string | null>;
  /** Called when we detect enrollment (e.g. after PayHero callback). Parent should refetch enrollment so the page updates without refresh. */
  onEnrollmentConfirmed?: () => void | Promise<void>;
}

export function EnrollModal({
  open,
  onClose,
  courseTitle,
  courseId,
  priceKes,
  isAuthenticated,
  onSignIn,
  userEmail,
  userName,
  onStartLearning,
  getLearnerId,
  onEnrollmentConfirmed,
}: EnrollModalProps) {
  const { formatKesPrice } = useCurrency();
  const [email, setEmail] = useState(userEmail ?? '');
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState(userName ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(false);
  const [waitingForCallback, setWaitingForCallback] = useState(false);
  const pollAbortRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // When pendingPayment and we can poll: wait for callback, then update parent and show success (no refresh).
  useEffect(() => {
    if (!open || !pendingPayment || !getLearnerId || !onEnrollmentConfirmed) return;
    pollAbortRef.current = false;
    setWaitingForCallback(true);
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const poll = async () => {
      try {
        const learnerId = await getLearnerId();
        if (!learnerId) return;
        const status = await lmsGetEnrollmentStatus(learnerId, courseId);
        if (pollAbortRef.current) return;
        if (status.enrolled) {
          if (intervalId) clearInterval(intervalId);
          intervalId = null;
          await onEnrollmentConfirmed();
          if (pollAbortRef.current) return;
          setPendingPayment(false);
          setWaitingForCallback(false);
          setSuccess(true);
        }
      } catch {
        // ignore; next poll will retry
      }
    };
    poll();
    intervalId = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      pollAbortRef.current = true;
      if (intervalId) clearInterval(intervalId);
      setWaitingForCallback(false);
    };
  }, [open, pendingPayment, courseId, getLearnerId, onEnrollmentConfirmed]);

  if (!open) return null;

  const formattedPrice =
    Number.isFinite(priceKes) && priceKes > 0
      ? formatKesPrice(priceKes)
      : 'Contact us for pricing';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated) {
      onSignIn();
      return;
    }
    if (!courseId) return;
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedEmail || !trimmedPhone) return;
    setSubmitting(true);
    setPendingPayment(false);
    try {
      const result = await enrollWithPayHero({
        courseId,
        email: trimmedEmail,
        phone: trimmedPhone,
        fullName: fullName.trim() || userName || trimmedEmail.split('@')[0] || 'Learner',
      });
      if (result.pending) {
        setPendingPayment(true);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Enrollment failed. Please try again.';
      toast.error(message, { duration: 6000 });
      console.error('Enroll with PayHero failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-card p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="enroll-modal-title"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="enroll-modal-title" className="text-xl font-display font-bold text-foreground">
            Enroll in {courseTitle}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="w-5 h-5" />
          </Button>
        </div>
        {!isAuthenticated ? (
          <>
            <p className="text-muted-foreground mb-6">
              Sign in with your account to enroll and access all modules. Use Google to continue.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                className="w-full bg-accent text-accent-foreground hover:bg-gold-light rounded-xl h-12 font-semibold"
                onClick={() => {
                  onClose();
                  onSignIn();
                }}
              >
                Sign in with Google
              </Button>
              <Button variant="outline" className="w-full" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        ) : pendingPayment ? (
          <>
            <p className="text-muted-foreground mb-6">
              {waitingForCallback
                ? 'Check your phone and complete the M-Pesa prompt. We\'re waiting for confirmation—the page will update automatically when payment is received.'
                : 'Check your phone and complete the M-Pesa prompt. Once payment is done, you\'ll be enrolled and we\'ll email your receipt. You can close this and refresh the page after paying.'}
            </p>
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={onClose}
              disabled={waitingForCallback}
            >
              {waitingForCallback ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                  Waiting for confirmation…
                </>
              ) : (
                'Done'
              )}
            </Button>
          </>
        ) : success ? (
          <>
            <p className="text-muted-foreground mb-6">
              Payment received. You&apos;re enrolled in this course, and a receipt has been emailed to you.
            </p>
            <Button
              className="w-full bg-accent text-accent-foreground hover:bg-gold-light rounded-xl"
              onClick={() => {
                onStartLearning?.();
                onClose();
              }}
            >
              Start learning
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Complete your enrollment by confirming your contact details and fees. We&apos;ll use these to send your
              receipt and course updates.
            </p>
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">Course fee</span>
              <span className="font-semibold text-foreground">{formattedPrice}</span>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Full name</label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Phone (for payment & updates)</label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+254 7xx xxx xxx"
              />
            </div>
            <div className="flex flex-col gap-3 pt-2">
              <Button
                type="submit"
                className="w-full bg-accent text-accent-foreground hover:bg-gold-light rounded-xl h-12 font-semibold"
                disabled={submitting}
              >
                {submitting ? 'Processing…' : 'Pay & Enroll'}
              </Button>
              <Button variant="outline" className="w-full" type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
